import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { CareType, CareTypes } from 'src/constants/CareTypes';
import useCareTypeFeatures from 'src/hooks/useCareTypeFeatures';
import {
    selectFacilityCareZones,
    selectFacilityId,
    selectUserCareZones,
} from 'src/store/configSlice';
import { CareZone, Unit, useUnitsSearchQuery } from 'src/store/sageAdminApi';
import { isUnitInCareZone } from 'src/utils/careZoneUtils';

type CareZoneWithUnits = CareZone & {
    units: Unit[];
};

const isCareZoneIndependentLiving = (careZone: CareZoneWithUnits) =>
    careZone?.name?.toLowerCase() === 'independent living' ||
    careZone?.unitTags?.careTypes?.includes(CareTypes.IndependentLiving) ||
    careZone?.unitTags?.wards?.includes(CareTypes.IndependentLiving) ||
    careZone.units?.some((unit) => unit?.careType === CareTypes.IndependentLiving);

const isCareZoneOnlyIndependentLiving = (careZone: CareZoneWithUnits) => {
    if (!careZone.units || careZone.units.length === 0) {
        return (
            careZone?.name?.toLowerCase() === 'independent living' ||
            careZone?.unitTags?.careTypes?.includes(CareTypes.IndependentLiving) ||
            careZone?.unitTags?.wards?.includes(CareTypes.IndependentLiving)
        );
    }
    return careZone.units.every((unit) => unit?.careType === CareTypes.IndependentLiving);
};

const isCareZoneLongTermCare = (careZone: CareZoneWithUnits) =>
    careZone?.name?.toLowerCase() === 'long term care' ||
    careZone?.unitTags?.careTypes?.includes(CareTypes.LongTermCare) ||
    careZone?.unitTags?.wards?.includes(CareTypes.LongTermCare) ||
    careZone.units.some((unit) => unit?.careType === CareTypes.LongTermCare);

const isCareZoneShortTermRehab = (careZone: CareZoneWithUnits) =>
    careZone?.name?.toLowerCase() === 'short term rehab' ||
    careZone?.unitTags?.careTypes?.includes(CareTypes.ShortTermRehab) ||
    careZone?.unitTags?.wards?.includes(CareTypes.ShortTermRehab) ||
    careZone.units.some((unit) => unit?.careType === CareTypes.ShortTermRehab);

const isCareZoneSkilledNursing = (careZone: CareZoneWithUnits) =>
    careZone?.name?.toLowerCase() === 'skilled nursing' ||
    careZone?.unitTags?.careTypes?.includes(CareTypes.SkilledNursing) ||
    careZone?.unitTags?.wards?.includes(CareTypes.SkilledNursing) ||
    careZone.units.some((unit) => unit?.careType === CareTypes.SkilledNursing);

function useCareZones() {
    const facilityId = useSelector(selectFacilityId);
    const facilityCareZones = useSelector(selectFacilityCareZones);
    const userCareZones = useSelector(selectUserCareZones);
    const { getFeatures } = useCareTypeFeatures();

    const { data: unitSearchResults, isLoading: isLoadingUnits } = useUnitsSearchQuery(
        { unitSearchRequest: { facilityIds: [facilityId] } },
        { skip: !facilityId }
    );
    const { units } = unitSearchResults || {};

    const careZonesIdMap = useMemo(() => {
        const map = new Map<string, CareZone>();
        (facilityCareZones || []).forEach((careZone) => {
            map.set(careZone.id!, careZone);
        });
        return map;
    }, [facilityCareZones]);

    const { careZonesWithUnits, careZonesWithoutUnits } = useMemo(
        () =>
            (facilityCareZones || []).reduce(
                (
                    acc: {
                        careZonesWithUnits: CareZoneWithUnits[];
                        careZonesWithoutUnits: CareZone[];
                    },
                    careZone
                ) => {
                    const unitsInCareZone = (units || []).filter((unit) =>
                        isUnitInCareZone(unit, careZone)
                    );

                    if (!unitsInCareZone) return acc;

                    const updatedCareZone = {
                        ...careZone,
                        units: unitsInCareZone,
                    } as CareZoneWithUnits;
                    if (unitsInCareZone.length > 0) {
                        acc.careZonesWithUnits.push(updatedCareZone);
                    } else {
                        acc.careZonesWithoutUnits.push(updatedCareZone);
                    }

                    return acc;
                },
                { careZonesWithUnits: [] as CareZoneWithUnits[], careZonesWithoutUnits: [] }
            ),
        [facilityCareZones, units]
    );

    const userCareZonesWithUnits = useMemo(() => {
        const zoneMap = new Map(careZonesWithUnits.map((zone) => [zone.id, zone]));

        return userCareZones.map((userCareZone: CareZone) => {
            const matchedZone = zoneMap.get(userCareZone.id);

            return {
                ...userCareZone,
                units: matchedZone?.units || [],
            } as CareZoneWithUnits;
        });
    }, [careZonesWithUnits, userCareZones]);

    const userCanChangeCareZones = useMemo(() => {
        const userHasSelectedCareZones = userCareZones.length > 0;

        if (
            careZonesWithUnits.length > 1 ||
            (careZonesWithUnits.length > 0 && !userHasSelectedCareZones)
        ) {
            return true;
        }

        return careZonesWithUnits.some((facilityCareZone) =>
            userCareZones.every((userCareZone: CareZone) => userCareZone.id !== facilityCareZone.id)
        );
    }, [careZonesWithUnits, userCareZones]);

    const userCareTypes = useMemo(() => {
        const careTypeSet = new Set<CareType>();

        for (const careZone of userCareZonesWithUnits) {
            const careTypes = (careZone.unitTags?.careTypes as CareType[]) || [];
            const wards = (careZone.unitTags?.wards as CareType[]) || [];

            careTypes.forEach((type) => careTypeSet.add(type));
            wards.forEach((type) => careTypeSet.add(type));
        }

        const allUnits = userCareZonesWithUnits.flatMap((zone) => zone.units);
        for (const unit of allUnits) {
            if (unit?.careType) {
                careTypeSet.add(unit.careType as CareType);
            }
        }

        return Array.from(careTypeSet);
    }, [userCareZonesWithUnits]);

    const isIndependentLiving = useMemo(
        () => userCareZonesWithUnits.some(isCareZoneIndependentLiving),
        [userCareZonesWithUnits]
    );

    const isCheckInsEnabled = userCareZonesWithUnits.some((zone) =>
        zone.units.some((unit) => getFeatures(unit?.careType).checkIns.isEnabled)
    );

    const isSkilledNursing = useMemo(
        () =>
            userCareZonesWithUnits.some(
                (zone: CareZoneWithUnits) =>
                    isCareZoneSkilledNursing(zone) ||
                    isCareZoneLongTermCare(zone) ||
                    isCareZoneShortTermRehab(zone)
            ),
        [userCareZonesWithUnits]
    );

    const isOnlyIndependentLiving = useMemo(
        () => userCareZonesWithUnits.every(isCareZoneOnlyIndependentLiving),
        [userCareZonesWithUnits]
    );

    const independentLivingCareZones = useMemo(
        () => careZonesWithUnits.filter(isCareZoneIndependentLiving),
        [careZonesWithUnits]
    );

    return {
        careZones: facilityCareZones,
        careZonesIdMap,
        careZonesWithUnits,
        careZonesWithoutUnits,
        independentLivingCareZones,
        isLoading: isLoadingUnits,
        isCheckInsEnabled,
        isIndependentLiving,
        isSkilledNursing,
        isOnlyIndependentLiving,
        userCareZones,
        userCareTypes,
        userCanChangeCareZones,
    };
}

export default useCareZones;
